import { __assign, __awaiter, __generator } from "tslib";
import { FiasDivision, FiasFields, FiasSchema, } from '../types';
import { FiasLogger } from '../logger/FiasLogger';
import { fetch } from '../../../lib/fetch';
import { FiasAddress } from '../models/FiasAddress';
import { FiasAPIResultFactory } from './FiasAPIResultFactory';
import { defaultDivision, defaultSchema } from '../constants/defaults';
import { FiasAddressElement } from '../models/FiasAddressElement';
import { FiasData } from '../models/FiasData';
var FiasAPI = /** @class */ (function () {
    function FiasAPI(_a) {
        var _this = this;
        var _b = _a === void 0 ? {} : _a, baseUrl = _b.baseUrl, version = _b.version, division = _b.division, schema = _b.schema, suggestUnknownParts = _b.suggestUnknownParts, suggestUnknownLevel = _b.suggestUnknownLevel;
        this.regionsPromise = null;
        this.baseUrl = '';
        this.version = '';
        this.defaultQuery = {};
        this.division = defaultDivision;
        this.schema = defaultSchema;
        this.verify = function (address) {
            var verifyUnknownLevel = _this.suggestUnknownLevel || (_this.suggestUnknownParts && 'House');
            var query = __assign(__assign({}, _this.defaultQuery), { directParent: true, search: false, verifyUnknownLevel: verifyUnknownLevel });
            return _this.send("verify?" + FiasAPI.createQuery(query), {
                method: 'POST',
                headers: {
                    Accept: 'application/json',
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify([address.convertForVerification()]),
            }).then(function (_a) {
                var success = _a.success, data = _a.data, error = _a.error;
                if (success && data) {
                    var _b = data[0] || {}, _c = _b.address, verifiedAddress = _c === void 0 ? {} : _c, _d = _b.isValid, isValid = _d === void 0 ? false : _d, invalidLevel = _b.invalidLevel;
                    return FiasAPIResultFactory.success(__assign({ address: _this.handleAddressResponse(verifiedAddress), isValid: isValid }, (invalidLevel ? { invalidLevel: _this.formatInvalidLevel(invalidLevel) } : {})));
                }
                else {
                    return FiasAPIResultFactory.fail(error && error.message);
                }
            });
        };
        this.search = function (options) { return __awaiter(_this, void 0, void 0, function () {
            var result, success, data, error;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0: return [4 /*yield*/, this.searchAddress(options)];
                    case 1:
                        result = _a.sent();
                        success = result.success, data = result.data, error = result.error;
                        if (success && data) {
                            return [2 /*return*/, FiasAPIResultFactory.success(data.map(this.handleAddressResponse))];
                        }
                        else {
                            return [2 /*return*/, FiasAPIResultFactory.fail(error && error.message)];
                        }
                        return [2 /*return*/];
                }
            });
        }); };
        this.searchAddress = function (_a) {
            var fiasId = _a.fiasId, searchText = _a.searchText, field = _a.field, parentFiasId = _a.parentFiasId, limit = _a.limit, fullAddress = _a.fullAddress, directParent = _a.directParent;
            var query = __assign(__assign({}, _this.defaultQuery), { prefix: searchText, actual: true, parentFiasId: parentFiasId,
                limit: limit,
                fullAddress: fullAddress,
                directParent: directParent });
            var emptyResult = {
                success: true,
                status: 200,
                data: [],
            };
            if (fiasId) {
                return _this.resolveFiasId(fiasId);
            }
            if (searchText) {
                if (!field) {
                    return _this.resolveAddress(searchText, limit);
                }
                else {
                    switch (field) {
                        case FiasFields.region:
                            return _this.searchRegions(searchText);
                        case FiasFields.house:
                            return _this.searchHouse(__assign(__assign({}, query), { schema: FiasSchema.Fias, suggestUnknownParts: _this.suggestUnknownParts || _this.suggestUnknownLevel === 'House' }));
                        case FiasFields.garhouse:
                            return _this.searchHouse(__assign(__assign({}, query), { schema: FiasSchema.Gar, suggestUnknownParts: _this.suggestUnknownParts || _this.suggestUnknownLevel === 'House' }));
                        case FiasFields.stead:
                            return _this.searchStead(query);
                        case FiasFields.room:
                            return _this.searchRoom(__assign(__assign({}, query), { suggestUnknownParts: _this.suggestUnknownParts || !!_this.suggestUnknownLevel }));
                        default: {
                            var levels = _this.getSearchLevels(field, query);
                            return _this.searchAddressObject(__assign(__assign({}, query), { levels: levels }));
                        }
                    }
                }
            }
            return Promise.resolve(emptyResult);
        };
        this.searchCountry = function (query) {
            return _this.send("countries?" + FiasAPI.createQuery(query));
        };
        this.send = function (path, params) {
            if (params === void 0) { params = {}; }
            var resultPromise = _this.baseUrl
                ? fetch("" + _this.baseUrl + path, __assign({ method: 'GET' }, params)).then(function (result) {
                    return result.ok
                        ? result.json().then(function (data) { return FiasAPIResultFactory.success(data); })
                        : Promise.reject(new Error(FiasLogger.warnings.fetchError));
                })
                : Promise.reject(new Error(FiasLogger.warnings.noBaseUrl));
            return resultPromise.catch(function (_a) {
                var message = _a.message;
                FiasLogger.log(message);
                return FiasAPIResultFactory.fail(message);
            });
        };
        this.resolveFiasId = function (fiasId) {
            return _this.send("addresses/structural/" + fiasId + "?" + FiasAPI.createQuery(_this.defaultQuery)).then(function (result) {
                var success = result.success, data = result.data, error = result.error;
                if (success && data) {
                    return FiasAPIResultFactory.success([data]);
                }
                else {
                    return FiasAPIResultFactory.fail(error && error.message);
                }
            });
        };
        this.searchAddressObject = function (query) {
            return _this.send("addresses?" + FiasAPI.createQuery(query));
        };
        this.resolveAddress = function (address, limit, level) {
            if (level === void 0) { level = FiasFields.apartment; }
            return _this.send("addresses/search?" + FiasAPI.createQuery(__assign(__assign({}, _this.defaultQuery), { suggestUnknownParts: _this.suggestUnknownParts, suggestUnknownLevel: _this.suggestUnknownLevel, address: address,
                limit: limit,
                level: level })));
        };
        this.searchStead = function (query) {
            return _this.send("steads?" + FiasAPI.createQuery(query)).then(function (result) {
                var success = result.success, data = result.data, error = result.error;
                if (success && data) {
                    return FiasAPIResultFactory.success(data.map(function (stead) {
                        return {
                            stead: stead,
                        };
                    }));
                }
                else {
                    return FiasAPIResultFactory.fail(error && error.message);
                }
            });
        };
        this.searchHouse = function (query) {
            return _this.send("houses?" + FiasAPI.createQuery(query)).then(function (result) {
                var success = result.success, data = result.data, error = result.error;
                if (success && data) {
                    return FiasAPIResultFactory.success(data.map(function (house) {
                        var _a;
                        return _a = {},
                            _a[query.schema === FiasSchema.Gar ? FiasFields.garhouse : FiasFields.house] = house,
                            _a;
                    }));
                }
                else {
                    return FiasAPIResultFactory.fail(error && error.message);
                }
            });
        };
        this.searchRoom = function (query) {
            return _this.send("rooms?" + FiasAPI.createQuery(query)).then(function (result) {
                var success = result.success, data = result.data, error = result.error;
                if (success && data) {
                    return FiasAPIResultFactory.success(data.map(function (room) {
                        return {
                            room: room,
                        };
                    }));
                }
                else {
                    return FiasAPIResultFactory.fail(error && error.message);
                }
            });
        };
        this.searchRegions = function (searchText) {
            if (!_this.regionsPromise) {
                _this.regionsPromise = _this.send('addresses/regions');
            }
            if (!searchText) {
                return _this.regionsPromise;
            }
            var isStartsWithSearchText = function (str) {
                return str && str.toLowerCase().includes(searchText.toLowerCase());
            };
            return _this.regionsPromise.then(function (result) {
                var success = result.success, data = result.data;
                if (success && data) {
                    return FiasAPIResultFactory.success(data.filter(function (address) {
                        var _a;
                        if (address.region) {
                            var addressData = new FiasData(address.region);
                            var _b = new FiasAddressElement(FiasFields.region, addressData.name, addressData), name_1 = _b.name, data_1 = _b.data;
                            return isStartsWithSearchText(name_1) || isStartsWithSearchText((_a = data_1 === null || data_1 === void 0 ? void 0 : data_1.code) !== null && _a !== void 0 ? _a : '');
                        }
                    }));
                }
                return result;
            });
        };
        this.getSearchLevels = function (field, _a) {
            var searchText = _a.prefix;
            var levels = [field];
            if (!searchText)
                return levels;
            if (_this.isMunicipalDivision && field === FiasFields.city) {
                // some cities are stored as districts in the DB
                // so we have to search them there
                // see https://yt.skbkontur.ru/issue/IF-165
                levels.push(FiasFields.district);
            }
            return levels;
        };
        // TODO: incapsulate correction inside FiasAddress
        this.handleAddressResponse = function (response) {
            var address = FiasAddress.createFromResponse(response);
            var correctedAddress = address.convertToCorrectFields({ division: _this.division });
            return FiasAddress.fieldsToResponse(correctedAddress.fields);
        };
        this.baseUrl = baseUrl || this.baseUrl;
        this.version = version || this.version;
        this.division = division || this.division;
        this.schema = schema || this.schema;
        this.suggestUnknownParts = suggestUnknownParts;
        this.suggestUnknownLevel = suggestUnknownLevel;
        this.defaultQuery = __assign(__assign({}, this.defaultQuery), { version: version, division: division, schema: schema });
    }
    Object.defineProperty(FiasAPI.prototype, "isMunicipalDivision", {
        get: function () {
            return this.division === FiasDivision.Municipal;
        },
        enumerable: false,
        configurable: true
    });
    FiasAPI.prototype.formatInvalidLevel = function (invalidLevel) {
        var level = invalidLevel.toLocaleLowerCase();
        if (level === FiasFields.house && this.schema === FiasSchema.Gar) {
            return FiasFields.garhouse;
        }
        return level;
    };
    FiasAPI.createFromAPI = function (api, options) {
        var baseUrl = api.baseUrl, version = api.version, division = api.division, schema = api.schema;
        return new FiasAPI(__assign({ baseUrl: baseUrl, version: version, division: division, schema: schema }, options));
    };
    FiasAPI.createQuery = function (query) {
        var params = [];
        for (var key in query) {
            if (Object.prototype.hasOwnProperty.call(query, key)) {
                var param = query[key];
                if (param !== undefined) {
                    if (key === 'levels' && Array.isArray(param)) {
                        for (var _i = 0, param_1 = param; _i < param_1.length; _i++) {
                            var level = param_1[_i];
                            params.push("level[]=" + encodeURIComponent(level));
                        }
                    }
                    else {
                        params.push(key + "=" + encodeURIComponent("" + param));
                    }
                }
            }
        }
        return params.join('&');
    };
    return FiasAPI;
}());
export { FiasAPI };
